import React from 'react';
import { Placeholder, VisitorIdentification } from '@sitecore-jss/sitecore-jss-react';
import Helmet from 'react-helmet';
import './assets/app.css';
import loadable from '@loadable/component';
import StarsRatingMobile from './components/SharedComponents/StarRatingMobile';
import JsonLdData from '../src/components/SharedComponents/JsonLd/JsonLdData';
import { getCoveoToken } from '@arm/ce-shared-components';
import config from './temp/config';
import { UseGlobalState } from './context';
import { CredentialAlert } from '@arm/ce-shared-components';
import fastDeepEqual from 'fast-deep-equal/es6/react';
import { getOrganizationEndpoints } from '@coveo/headless';
import { VirtualFAE } from './components/SharedComponents/VirtualFAE';
import { getVirtualFAEConfig } from './config/virtualFAEConfig';
import { isSupportApplication } from './lib/ApplicationTag';
import { getAuthCoveoUser } from './utils/authAccessToken';

// VirtualFAE config is sourced from build-time-frozen env vars (temp/config.js),
// so resolve it once at module load instead of on every render.
const VIRTUAL_FAE_DATA = getVirtualFAEConfig();

const Layout = ({ route }) => {
  const { user } = UseGlobalState();
  const isSupport = isSupportApplication(route);
  if (typeof window !== 'undefined') {
    if (window.OnetrustActiveGroups && window.OnetrustActiveGroups.includes('C0002')) {
      const script = document.createElement('script');
      script.src = 'https://static.cloud.coveo.com/coveo.analytics.js/2/coveoua.js';
      var currenturl = window.location.href;
      script.onload = async function () {
        const coveoToken = await getCoveoToken(`${config.REACT_APP_COVEOGETSEARCHTOKENURL}`, {
          ...getAuthCoveoUser(user),
        });
        const organizationAnalyticsEndpoint = getOrganizationEndpoints(
          `${config.REACT_APP_COVEOORGANIZATIONID}`
        ).analytics;
        window.coveoua('init', coveoToken, organizationAnalyticsEndpoint);
        window.coveoua('send', 'view', {
          contentIdKey: '@clickableuri',
          contentIdValue: currenturl,
          contentType: 'DeveloperDocumentation',
        });
      };
      document.head.appendChild(script);
    }
  }

  return (
    <React.Fragment>
      {/* react-helmet enables setting <head> contents, like title and OG meta tags */}
      <Helmet>
        <title>
          {(route.fields && route.fields.pageTitle && route.fields.pageTitle?.value) || 'Page'}
        </title>
        <meta name="description" content={route.fields.pageDescription?.value}></meta>
        <meta property="og:description" content={route.fields?.pageDescription?.value}></meta>
        <meta property="og:image" content={route.fields?.pageSocialImage?.value?.src} />
        <meta
          property="og:image:alt"
          content={route.fields?.pageSocialImage?.value?.alt || 'Social preview image'}
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:image" content={route.fields?.pageSocialImage?.value?.src} />
      </Helmet>

      <JsonLdData {...route} />

      {/* VirtualFAE (chat-ai) widget -- only mount on Support-tagged pages. */}
      {isSupport && <VirtualFAE data={VIRTUAL_FAE_DATA} />}

      {/*
      VisitorIdentification is necessary for Sitecore Analytics to determine if the visitor is a robot.
      If Sitecore XP (with xConnect/xDB) is used, this is required or else analytics will not be collected for the JSS app.
      For XM (CMS-only) apps, this should be removed.

      VI detection only runs once for a given analytics ID, so this is not a recurring operation once cookies are established.
    */}

      <div className="c-app u-flex u-flex-column">
        <div
          role="navigation"
          className="dev-c-skip-navigation u-margin-0 u-padding-0 u-position-relative"
          aria-label="skip navigation"
        >
          <a href="#header" data-track="true" data-track-value="Skip Navigation (Press Enter)">
            Skip Navigation (Press Enter)
          </a>
          <a href="#content" data-track="true" data-track-value="Skip to Content (Press Enter)">
            Skip to Content (Press Enter)
          </a>
        </div>
        <div role="navigation">
          <Placeholder name="jss-header" rendering={route} />
        </div>
        <Placeholder name="jss-masthead" rendering={route} />
        {route.itemId !== '4afd9f1d-871a-5874-a803-7c8af50a0d03' ? <CredentialAlert /> : ''}
        <main id="content" className="u-flex-auto u-position-relative">
          <Placeholder name="jss-main" rendering={route} />
          {route.itemId !== '4afd9f1d-871a-5874-a803-7c8af50a0d03' ? (
            <StarsRatingMobile uid={route.itemId} />
          ) : (
            ''
          )}
        </main>
        <div className="footer-wrapper">
          <Placeholder name="jss-footer" rendering={route} />
        </div>
      </div>
    </React.Fragment>
  );
};
const propsAreEqual = (prevProps, nextProps) => {
  if (fastDeepEqual(prevProps.route, nextProps.route)) return true;

  return false;
};
export default React.memo(Layout, propsAreEqual);
